import { useEffect, useState, useCallback, useRef } from 'react';
import api from '../api/client';
import TasksByTagProgressPanel from './TasksByTagProgressPanel';

const DEBOUNCE_MS = 600;

function getCsrfCookie() {
  if (!document.cookie) return '';
  const match = document.cookie.split(';').map((c) => c.trim()).find((c) => c.startsWith('csrftoken='));
  return match ? decodeURIComponent(match.slice('csrftoken='.length)) : '';
}

/** PATCH, который переживает уход со страницы (keepalive). */
function flushNotesKeepalive(studentId, notes) {
  const csrf = getCsrfCookie();
  fetch(`/api/teacher/students/${studentId}/topic-progress-notes/`, {
    method: 'PATCH',
    credentials: 'same-origin',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      ...(csrf ? { 'X-CSRFToken': csrf } : {}),
    },
    body: JSON.stringify({ notes }),
  }).catch(() => {});
}

/**
 * Заметки преподавателей + журнал задач по темам.
 * studentId — чей прогресс; canEdit — учитель редактирует свою заметку;
 * showJournal — вторая внутренняя вкладка (у родителя обычно false);
 * onSelectTaskSlug — клик по задаче из журнала тегов (карточка ученика).
 */
export default function StudentTopicProgressPanel({
  studentId,
  canEdit = false,
  showJournal = true,
  onSelectTaskSlug,
}) {
  const [mainTab, setMainTab] = useState('notes'); // 'notes' | 'journal'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [serverNotes, setServerNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveOk, setSaveOk] = useState(false);

  const targetStudentId = Number(studentId);
  const debounceRef = useRef(null);
  const saveSeqRef = useRef(0);
  const editNotesRef = useRef('');
  const serverNotesRef = useRef('');
  const canEditRef = useRef(canEdit);
  const targetStudentIdRef = useRef(targetStudentId);

  editNotesRef.current = editNotes;
  serverNotesRef.current = serverNotes;
  canEditRef.current = canEdit;
  targetStudentIdRef.current = targetStudentId;

  const loadNotes = useCallback(async () => {
    if (!targetStudentId || Number.isNaN(targetStudentId)) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/teacher/students/${targetStudentId}/topic-progress-notes/`);
      const list = Array.isArray(res.data) ? res.data : [];
      setItems(list);
      const mine = list.find((row) => row.is_me);
      const text = mine?.notes ?? '';
      setEditNotes(text);
      setServerNotes(text);
      setSaveError('');
      setSaveOk(false);
    } catch (e) {
      const msg = e.response?.data?.detail || e.response?.data?.message || 'Не удалось загрузить заметки.';
      setError(typeof msg === 'string' ? msg : 'Не удалось загрузить заметки.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [targetStudentId]);

  useEffect(() => {
    if (!targetStudentId || Number.isNaN(targetStudentId)) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    saveSeqRef.current += 1;
    setEditNotes('');
    setServerNotes('');
    setSaveError('');
    setSaveOk(false);
    setSaving(false);
    loadNotes();
  }, [targetStudentId, loadNotes]);

  useEffect(() => {
    if (!saveOk) return;
    const t = setTimeout(() => setSaveOk(false), 2000);
    return () => clearTimeout(t);
  }, [saveOk]);

  const saveNotes = useCallback(async (text, forStudentId) => {
    const sid = forStudentId ?? targetStudentIdRef.current;
    if (!canEditRef.current || !sid || Number.isNaN(sid)) return;
    if (text === serverNotesRef.current) return;

    const seq = ++saveSeqRef.current;
    setSaving(true);
    setSaveError('');
    try {
      const res = await api.patch(`/teacher/students/${sid}/topic-progress-notes/`, {
        notes: text,
      });
      if (seq !== saveSeqRef.current || targetStudentIdRef.current !== sid) return;

      const saved = res.data?.notes ?? text;
      const updatedAt = res.data?.updated_at ?? null;
      setServerNotes(saved);
      setItems((prev) => prev.map((row) => (
        row.is_me
          ? { ...row, notes: saved, updated_at: updatedAt }
          : row
      )));
      setSaveOk(true);
    } catch (e) {
      if (seq !== saveSeqRef.current || targetStudentIdRef.current !== sid) return;
      const msg = e.response?.data?.detail || 'Не удалось сохранить.';
      setSaveError(typeof msg === 'string' ? msg : 'Не удалось сохранить.');
    } finally {
      if (seq === saveSeqRef.current && targetStudentIdRef.current === sid) {
        setSaving(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!canEdit) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (editNotes === serverNotes) return undefined;

    const sid = targetStudentId;
    debounceRef.current = setTimeout(() => {
      saveNotes(editNotes, sid);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [editNotes, serverNotes, canEdit, targetStudentId, saveNotes]);

  // При уходе с карточки / смене ученика — сразу отправить черновик (keepalive).
  useEffect(() => {
    const sidAtMount = targetStudentId;
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      const text = editNotesRef.current;
      if (!canEditRef.current || !sidAtMount || Number.isNaN(sidAtMount)) return;
      if (text === serverNotesRef.current) return;
      saveSeqRef.current += 1;
      flushNotesKeepalive(sidAtMount, text);
    };
  }, [targetStudentId]);

  if (loading && !error) {
    return (
      <div className="topic-progress-panel-loading text-secondary">
        <p>Загрузка…</p>
      </div>
    );
  }

  const effectiveTab = showJournal ? mainTab : 'notes';

  return (
    <div className="student-topic-progress-panel">
      {showJournal && (
        <div className="topic-progress-main-tabs" role="tablist" aria-label="Вид прогресса">
          <button
            type="button"
            role="tab"
            aria-selected={effectiveTab === 'notes'}
            className={`btn btn-sm ${effectiveTab === 'notes' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setMainTab('notes')}
          >
            Заметки по темам
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={effectiveTab === 'journal'}
            className={`btn btn-sm ${effectiveTab === 'journal' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setMainTab('journal')}
          >
            Журнал задач по темам
          </button>
        </div>
      )}

      {effectiveTab === 'journal' && showJournal && (
        <div className="topic-progress-stub-card" role="tabpanel">
          <TasksByTagProgressPanel
            studentId={targetStudentId}
            onSelectTaskSlug={onSelectTaskSlug}
          />
        </div>
      )}

      {effectiveTab === 'notes' && (
        <div className="topic-progress-notes-panel" role="tabpanel">
          {error && <div className="alert alert-error">{error}</div>}
          {saveError && <div className="alert alert-error">{saveError}</div>}

          {!error && items.length === 0 && !loading && (
            <p className="text-secondary">
              Нет активных связей с преподавателями — пока нечего показать.
            </p>
          )}

          {items.map((item) => {
            const updatedLabel = item.updated_at
              ? new Date(item.updated_at).toLocaleString('ru-RU')
              : 'ещё не обновлялось';

            const inner = (
              <div className="progress-note-card-inner">
                <div className="progress-note-meta text-secondary">
                  Обновлено: {updatedLabel}
                </div>
                {canEdit && item.is_me ? (
                  <div className="form-group">
                    <label htmlFor={`notes-edit-${item.teacher_id}`}>Текст заметки</label>
                    <textarea
                      id={`notes-edit-${item.teacher_id}`}
                      className="form-control"
                      rows={8}
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      placeholder="Сохраняется автоматически"
                    />
                    <span className="save-indicator">
                      {saving ? 'Сохранение…' : saveOk ? '✓ Сохранено' : ''}
                    </span>
                  </div>
                ) : (
                  <pre className="progress-note-readonly">{item.notes || '—'}</pre>
                )}
              </div>
            );

            if (canEdit && !item.is_me) {
              return (
                <details key={item.teacher_id} className="progress-note-card">
                  <summary className="progress-note-summary">{item.teacher_display_name}</summary>
                  {inner}
                </details>
              );
            }

            return (
              <section key={item.teacher_id} className="progress-note-card progress-note-card-expanded">
                <h2 className="progress-note-title">{item.teacher_display_name}</h2>
                {inner}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
